import type { BoxProps } from '@mantine/core';
import { Box, createPolymorphicComponent } from '@mantine/core';
import { _omit } from '@epihub/core/src/fn/object';
import type { PropsWithChildren } from 'react';
import { styled } from '@epihub/web/src/_ctx/theme';
import type { Dim } from '@epihub/web/src/dimensions';
import { normalizeToPx } from '@epihub/web/src/dimensions';
import { type PropsWithClassName } from '@epihub/web/src/react/types';

type StructurePropsBase = {
  p?: Dim;
  py?: Dim;
  px?: Dim;
  pt?: Dim;
  mt?: Dim;
  mb?: Dim;
  m?: Dim;
  w?: Dim;
  mh?: Dim;
  ml?: Dim;
  maw?: Dim;
  miw?: Dim;
  column?: boolean;
  row?: boolean;
  vcenter?: boolean;
  hcenter?: boolean;
  grow?: true;
  end?: true;
  hfull?: true;
  wfull?: true;
};

export type StructureProps = PropsWithClassName<StructurePropsBase>;

export const Structure = (props: PropsWithChildren<StructureProps>) => {
  return (
    <RootBox
      {...props}
      p={props.p ? normalizeToPx(props.p) : undefined}
      py={props.py ? normalizeToPx(props.py) : undefined}
      px={props.px ? normalizeToPx(props.px) : undefined}
      pt={props.pt ? normalizeToPx(props.pt) : undefined}
      mt={props.mt ? normalizeToPx(props.mt) : undefined}
      mh={props.mh ? normalizeToPx(props.mh) : undefined}
      ml={props.ml ? normalizeToPx(props.ml) : undefined}
      maw={props.maw ? normalizeToPx(props.maw) : undefined}
      miw={props.miw ? normalizeToPx(props.miw) : undefined}
      mb={props.mb ? normalizeToPx(props.mb) : undefined}
      w={props.w ? normalizeToPx(props.w) : undefined}
      grow={props.grow}
      hfull={props.hfull}
      wfull={props.wfull}
      end={props.end}
    >
      {props.children}
    </RootBox>
  );
};

const omitProps = [
  'vcenter',
  'hcenter',
  'column',
  'row',
];

function isFlexbox({ vcenter, hcenter, row, column, grow, end }) {
  return vcenter || hcenter || row || column || grow || end;
}

const RootBox = createPolymorphicComponent<
  'div',
  BoxProps & StructurePropsBase
>(styled((props) => <Box {..._omit(props, omitProps)} />)<StructurePropsBase>`
  ${(p) => isFlexbox(p) ? 'display: flex' : ''};
  ${(p) => isFlexbox(p) && p.grow ? 'flex: 1' : ''};
  ${(p) => isFlexbox(p) ? `flex-direction: ${p.row ? 'row' : 'column'}` : ''};
  ${({ vcenter, row }) => vcenter ? (row ? 'align-items: center' : 'justify-content: center') : ''};
  ${({ hcenter, row }) => hcenter ? (row ? 'justify-content: center' : 'align-items: center') : ''};
  ${({ end }) => end ? 'align-items: flex-end' : ''};
  ${({ hfull }) => hfull ? 'height: 100%' : ''};
  ${({ wfull }) => wfull ? 'width: 100%' : ''};
`);
